import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Container,
    Avatar,
    Button,
    Tooltip,
    ListItem,
    Drawer,
    Divider,
    List,
    ListItemText,
    ListItemIcon,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GoogleIcon from "@mui/icons-material/Google";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useNavigate, useLocation } from "react-router-dom";

import useFirebaseAuth from "../utils/useFirebaseAuth";
import { signOut, auth, signInWithGoogle } from "../firebase";

const drawerWidth = 240;

const pages = [
    { displayName: "Hjem", url: "/" },
    { displayName: "Gjestebok", url: "/gjestebok" },
    { displayName: "Festival", url: "/festival" },
];

const festivalPages = [
    { displayName: "Program", url: "/festival/program" },
    { displayName: "Bar", url: "festival/bar" },
    { displayName: "Kart", url: "festival/kart" },
    {
        displayName: "Shotterud",
        url: "https://NikolaiDokken.github.io/Shotterud",
        external: true,
    },
];

const ResponsiveAppBar = ({ toggleTheme }) => {
    const theme = useTheme();
    const location = useLocation();
    const authUser = useFirebaseAuth();
    const navigate = useNavigate();
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const getRoutes = () => {
        return location.pathname.includes("festival") ? festivalPages : pages;
    };

    const DrawerHeader = styled("div")(({ theme }) => ({
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
        justifyContent: "space-between",
    }));

    return (
        <div>
            <AppBar
                position="fixed"
                elevation={0}
                sx={
                    location.pathname.includes("festival")
                        ? {
                              color: theme.palette.text.primary,
                              backgroundImage: 'url("/static/background.png")',
                              backgroundSize: "cover",
                          }
                        : {
                              background: theme.palette.background.default,
                              color: theme.palette.text.primary,
                          }
                }
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {location.pathname.includes("festival") ? (
                            <Box
                                sx={{
                                    width: 200,
                                    mr: 2,
                                    display: {
                                        xs: "none",
                                        md: "flex",
                                        cursor: "pointer",
                                    },
                                }}
                                onClick={() =>
                                    location.pathname.includes("festival") ? navigate("/festival") : navigate("/")
                                }
                            >
                                <img
                                    src={
                                        theme.palette.mode === "dark"
                                            ? "/static/logo_white.png"
                                            : "/static/logo_black.png"
                                    }
                                    style={{ width: "100%" }}
                                    alt="logo"
                                />
                            </Box>
                        ) : (
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{
                                    mr: 2,
                                    display: {
                                        xs: "none",
                                        md: "flex",
                                        cursor: "pointer",
                                    },
                                }}
                                onClick={() =>
                                    location.pathname.includes("festival") ? navigate("/festival") : navigate("/")
                                }
                            >
                                RESHJEM
                            </Typography>
                        )}

                        <Box
                            sx={{
                                flexGrow: 1,
                                display: { xs: "flex", md: "none" },
                            }}
                        >
                            <IconButton size="large" onClick={() => setDrawerOpen(true)} color="inherit">
                                <MenuIcon />
                            </IconButton>
                        </Box>
                        {location.pathname.includes("festival") ? (
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: {
                                        xs: "flex",
                                        md: "none",
                                        cursor: "pointer",
                                    },
                                }}
                                onClick={() =>
                                    location.pathname.includes("festival") ? navigate("/festival") : navigate("/")
                                }
                            >
                                <img src="/static/logo_white.png" style={{ width: 200 }} alt="logo" />
                            </Box>
                        ) : (
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    display: { xs: "flex", md: "none" },
                                    cursor: "pointer",
                                }}
                                onClick={() =>
                                    location.pathname.includes("festival") ? navigate("/festival") : navigate("/")
                                }
                            >
                                RESHJEM
                            </Typography>
                        )}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: { xs: "none", md: "flex" },
                            }}
                        >
                            {getRoutes().map((page) => (
                                <Button
                                    key={page.displayName}
                                    onClick={() =>
                                        page.external ? (window.location.href = page.url) : navigate(page.url)
                                    }
                                    sx={{
                                        my: 2,
                                        display: "block",
                                        color: "inherit",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {page.displayName}
                                </Button>
                            ))}
                        </Box>
                        <IconButton
                            sx={{
                                display: { xs: "none", md: "flex" },
                                mr: 2,
                                color: "inherit",
                            }}
                            onClick={toggleTheme}
                        >
                            {theme.palette.mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
                        </IconButton>
                        {authUser ? (
                            <Box sx={{ flexGrow: 0 }}>
                                <Tooltip title="Innstillinger">
                                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                        <Avatar alt={authUser.displayName} src={authUser.photoURL} />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    sx={{ mt: "45px" }}
                                    anchorEl={anchorElUser}
                                    anchorOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                    open={Boolean(anchorElUser)}
                                    onClose={handleCloseUserMenu}
                                >
                                    <MenuItem onClick={() => signOut(auth)}>
                                        <Typography textAlign="center">Logg av</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex" }}>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<GoogleIcon />}
                                    onClick={signInWithGoogle}
                                    sx={{ display: { xs: "none", md: "flex" } }}
                                >
                                    Logg inn
                                </Button>
                                <AccountCircleOutlinedIcon
                                    onClick={signInWithGoogle}
                                    sx={{ display: { xs: "flex", md: "none", cursor: "pointer" }, fontSize: 40 }}
                                />
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        boxSizing: "border-box",
                    },
                }}
                onClose={() => setDrawerOpen(false)}
                variant="temporary"
                anchor="left"
                open={drawerOpen}
            >
                <DrawerHeader>
                    <IconButton
                        sx={{
                            mr: 2,
                            color: "inherit",
                        }}
                        onClick={toggleTheme}
                    >
                        {theme.palette.mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
                    </IconButton>
                    <IconButton onClick={() => setDrawerOpen(!drawerOpen)} sx={{ color: "inherit" }}>
                        {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    {getRoutes().map((page) => (
                        <ListItem
                            button
                            key={page.displayName}
                            onClick={() => {
                                if (page.external) {
                                    window.location.href = page.url;
                                } else {
                                    navigate(page.url);
                                    setDrawerOpen(false);
                                }
                            }}
                        >
                            <ListItemText primary={page.displayName} />
                        </ListItem>
                    ))}
                    {location.pathname.indexOf("festival") > -1 && (
                        <ListItem
                            button
                            onClick={() => navigate("/")}
                            sx={{
                                position: "fixed",
                                bottom: 0,
                                left: 0,
                                width: drawerWidth,
                                mb: 1,
                            }}
                        >
                            <ListItemIcon>
                                <ChevronLeftIcon />
                            </ListItemIcon>
                            <ListItemText>Tilbake til hovedside</ListItemText>
                        </ListItem>
                    )}
                </List>
            </Drawer>
        </div>
    );
};
export default ResponsiveAppBar;
